/**
 * Leads API service — realtor mini-CRM (Phase 3).
 *
 * Frontend client for /api/v1/leads/*. Mirrors the realtor / listings
 * services: the backend's `{success, data, message, error}` envelope is
 * re-shaped here into `{success, data, error, errorCode}` where
 *   - `error` is the human-readable backend `message` (or a network/
 *     timeout copy for transport failures), used directly in toasts.
 *   - `errorCode` is best-effort: it carries the backend's structured
 *     `error` field (e.g. `LEAD_NOT_FOUND`, `VALIDATION_ERROR`) when
 *     the route emits one, and is `null` for unhandled 500s. Callers
 *     should branch on `errorCode` for routing decisions but never
 *     assume it's set — fall back to `error` for the user copy.
 *
 * Wire shape intentionally mirrors the backend's `Lead` row but with
 * ISO date strings (the Drizzle row carries Date objects; HTTP is
 * strings).
 */

import type { Listing } from "@/features/realtor/services/listings-api.service";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export type LeadType = "buyer" | "seller";
export type LeadTemperature = "hot" | "warm" | "cold";
export const LEAD_TYPES: readonly LeadType[] = ["buyer", "seller"];
export const LEAD_TEMPERATURES: readonly LeadTemperature[] = [
	"hot",
	"warm",
	"cold",
];
export type PipelineStage =
	| "new"
	| "contacted"
	| "viewing_scheduled"
	| "negotiating"
	| "closed_won"
	| "closed_lost";
export type ActivityType = "call" | "viewing" | "message" | "note" | "reminder";

// Stage ordering for the kanban. The visual identity (icon, accent
// rail, chip tone) lives in the page's STAGE_THEME map keyed by
// stage id — keeping it there avoids two sources of truth for stage
// colours and prevents drift.
export const PIPELINE_STAGES: ReadonlyArray<{ id: PipelineStage }> = [
	{ id: "new" },
	{ id: "contacted" },
	{ id: "viewing_scheduled" },
	{ id: "negotiating" },
	{ id: "closed_won" },
	{ id: "closed_lost" },
];

export interface Lead {
	id: string;
	realtorId: string;
	/** The address-book contact for this lead's number. */
	contactId: string | null;
	name: string;
	phoneCountryCode: string;
	phone: string;
	email: string | null;
	type: LeadType;
	source: string | null;
	budgetMin: number | null;
	budgetMax: number | null;
	temperature: LeadTemperature;
	pipelineStage: PipelineStage;
	stageChangedAt: string;
	notes: string | null;
	tags: string[];
	createdAt: string;
	updatedAt: string;
}

export interface LeadActivity {
	id: string;
	leadId: string;
	realtorId: string;
	type: ActivityType;
	title: string;
	body: string | null;
	scheduledAt: string | null;
	completedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

/** Snapshot of a linked listing — just enough to render in a lead card. */
export interface LeadLinkedListing {
	id: string;
	headline: string;
	propertyType: Listing["propertyType"];
	bhk: number | null;
	locality: string;
	city: string;
}

export interface LeadWithLinks {
	lead: Lead;
	listings: LeadLinkedListing[];
	activityCount: number;
	upcomingActivity: LeadActivity | null;
}

export type CreateLeadInput = {
	name: string;
	phone: string;
	phoneCountryCode?: string;
	email?: string | null;
	type: LeadType;
	source?: string | null;
	budgetMin?: number | null;
	budgetMax?: number | null;
	temperature?: LeadTemperature;
	notes?: string | null;
	tags?: string[];
	listingIds?: string[];
};

export type UpdateLeadInput = Partial<
	Omit<CreateLeadInput, "type" | "listingIds">
> & {
	type?: LeadType;
};

export interface CreateActivityInput {
	type: ActivityType;
	title: string;
	body?: string | null;
	scheduledAt?: string | null;
	completedAt?: string | null;
}

interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

export interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	data?: T;
}

export function buildFailure<T>(
	error: unknown,
	fallback: string,
): ApiResult<T> {
	if (error == null) {
		return { success: false, error: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	if (!err.response) {
		return {
			success: false,
			error:
				err.code === "ECONNABORTED" || err.message?.includes("timeout")
					? "Request timed out. Check your connection and try again."
					: "Couldn't reach the server. Check your connection and try again.",
			errorCode: "NETWORK_ERROR",
		};
	}
	const body = err.response.data;
	return {
		success: false,
		error: body?.message ?? fallback,
		errorCode: body?.error ?? null,
	};
}

// ----------------------------------------------------------------------
// READ
// ----------------------------------------------------------------------

export async function listMyLeads(): Promise<ApiResult<LeadWithLinks[]>> {
	try {
		const response = await apiClient.get<{
			data: { leads: LeadWithLinks[] };
		}>(`${API_BASE}/leads/me`);
		return { success: true, data: response.data.data.leads };
	} catch (error: unknown) {
		return buildFailure<LeadWithLinks[]>(error, "Failed to load leads");
	}
}

/** An open scheduled activity with the lead it belongs to. */
export interface ScheduledActivity {
	activity: LeadActivity;
	lead: Pick<Lead, "id" | "name" | "type" | "phone" | "phoneCountryCode">;
}

/**
 * Every open scheduled activity before `to` (exclusive), across all leads.
 * Omit `from` to include overdue items.
 */
export async function listMySchedule(range: {
	from?: Date;
	to: Date;
}): Promise<ApiResult<ScheduledActivity[]>> {
	try {
		const response = await apiClient.get<{
			data: { items: ScheduledActivity[] };
		}>(`${API_BASE}/leads/me/schedule`, {
			params: {
				...(range.from ? { from: range.from.toISOString() } : {}),
				to: range.to.toISOString(),
			},
		});
		return { success: true, data: response.data.data.items };
	} catch (error: unknown) {
		return buildFailure<ScheduledActivity[]>(error, "Failed to load schedule");
	}
}

export async function getMyLead(
	leadId: string,
): Promise<ApiResult<{ lead: Lead; listings: Listing[] }>> {
	try {
		const response = await apiClient.get<{
			data: { lead: Lead; listings: Listing[] };
		}>(`${API_BASE}/leads/${leadId}`);
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		return buildFailure<{ lead: Lead; listings: Listing[] }>(
			error,
			"Failed to load lead",
		);
	}
}

// ----------------------------------------------------------------------
// WRITE
// ----------------------------------------------------------------------

export async function createLead(
	input: CreateLeadInput,
): Promise<ApiResult<Lead>> {
	try {
		const response = await apiClient.post<{
			data: { lead: Lead };
		}>(`${API_BASE}/leads`, input);
		return { success: true, data: response.data.data.lead };
	} catch (error: unknown) {
		return buildFailure<Lead>(error, "Failed to create lead");
	}
}

export async function updateLead(
	leadId: string,
	input: UpdateLeadInput,
): Promise<ApiResult<Lead>> {
	try {
		const response = await apiClient.patch<{
			data: { lead: Lead };
		}>(`${API_BASE}/leads/${leadId}`, input);
		return { success: true, data: response.data.data.lead };
	} catch (error: unknown) {
		return buildFailure<Lead>(error, "Failed to update lead");
	}
}

export async function deleteLead(leadId: string): Promise<ApiResult<null>> {
	try {
		await apiClient.delete(`${API_BASE}/leads/${leadId}`);
		return { success: true, data: null };
	} catch (error: unknown) {
		return buildFailure<null>(error, "Failed to delete lead");
	}
}

// ----------------------------------------------------------------------
// PIPELINE
// ----------------------------------------------------------------------

export async function changeLeadStage(
	leadId: string,
	pipelineStage: PipelineStage,
): Promise<ApiResult<Lead>> {
	try {
		const response = await apiClient.post<{
			data: { lead: Lead };
		}>(`${API_BASE}/leads/${leadId}/stage`, { pipelineStage });
		return { success: true, data: response.data.data.lead };
	} catch (error: unknown) {
		return buildFailure<Lead>(error, "Failed to update lead stage");
	}
}

// ----------------------------------------------------------------------
// LISTING LINKS
// ----------------------------------------------------------------------

export async function linkListingToLead(
	leadId: string,
	listingId: string,
): Promise<ApiResult<null>> {
	try {
		await apiClient.post(`${API_BASE}/leads/${leadId}/listings`, {
			listingId,
		});
		return { success: true, data: null };
	} catch (error: unknown) {
		return buildFailure<null>(error, "Failed to link listing");
	}
}

export async function unlinkListingFromLead(
	leadId: string,
	listingId: string,
): Promise<ApiResult<null>> {
	try {
		await apiClient.delete(`${API_BASE}/leads/${leadId}/listings/${listingId}`);
		return { success: true, data: null };
	} catch (error: unknown) {
		return buildFailure<null>(error, "Failed to unlink listing");
	}
}

// ----------------------------------------------------------------------
// ACTIVITIES
// ----------------------------------------------------------------------

export async function listLeadActivities(
	leadId: string,
): Promise<ApiResult<LeadActivity[]>> {
	try {
		const response = await apiClient.get<{
			data: { activities: LeadActivity[] };
		}>(`${API_BASE}/leads/${leadId}/activities`);
		return { success: true, data: response.data.data.activities };
	} catch (error: unknown) {
		return buildFailure<LeadActivity[]>(error, "Failed to load activities");
	}
}

export async function createActivity(
	leadId: string,
	input: CreateActivityInput,
): Promise<ApiResult<LeadActivity>> {
	try {
		const response = await apiClient.post<{
			data: { activity: LeadActivity };
		}>(`${API_BASE}/leads/${leadId}/activities`, input);
		return { success: true, data: response.data.data.activity };
	} catch (error: unknown) {
		return buildFailure<LeadActivity>(error, "Failed to log activity");
	}
}

export async function deleteActivity(
	leadId: string,
	activityId: string,
): Promise<ApiResult<null>> {
	try {
		await apiClient.delete(
			`${API_BASE}/leads/${leadId}/activities/${activityId}`,
		);
		return { success: true, data: null };
	} catch (error: unknown) {
		return buildFailure<null>(error, "Failed to delete activity");
	}
}
